import { arrayToEntityDict, type LinkedPlayerBuildsListItem, type Survey, type User } from '@webapp2/shared/model'
import { localSettings, type LocalAuth } from '../main'
import { appRoutes, authRoutes, router, type OssoRouteLocation } from '../router'
import { state, type Organization, type PermissionGroup } from '../state'
import { post, postGetBody, type ProblemDetailsResponse } from '@webapp2/shared/services/api'
import { reset } from './app-service'
import { cache } from './cache'
import { trackEvent } from './log-rocket-service'
import { mapAdminPermissions, mapOrganizationPermissions } from './permissions-service'
import * as questBridge from './quest-bridge'
import * as signalrService from './signalr-service'

export type AuthenticatedResponse = {
  user: AuthResponseUser
  organizations: Organization[]
  featureSwitches: string[]
  organizationId: string
  permissionGroups: PermissionGroup[]
  userSettings: Record<string, unknown> | null
  survey: Survey | null
  latestPlayerBuildSubmittedToMeta: LinkedPlayerBuildsListItem | null
  redirectPath?: string | null
}

export type OrganizationPermissionGroups = {
  organizationId: string
  permissionGroupIds: string[]
}

export type AuthResponseUser = User & {
  organizationIds: string[]
  permissionGroupIds: string[]
  organizationPermissions: OrganizationPermissionGroups[]
}

type AuthType = 'direct' | 'qr' | 'health-stream' | 'fetch-user'

export async function authenticate(route: OssoRouteLocation) {
  if (localSettings?.auth?.enabled) {
    return await autoLocalAuth(localSettings.auth, route.fullPath)
  }

  const organizationId = route.params.organizationId ?? (await cache.get<string>('currentOrganizationId'))

  const response = await tryFetchUser(organizationId)
  if (response && 'user' in response) {
    return authenticated(response, 'fetch-user', route.meta.layout === 'auth' ? undefined : route.fullPath)
  }

  if (route.meta.layout !== 'auth' && !route.redirectedFrom?.meta.externalAuth) {
    localStorage.setItem('redirect', route.fullPath)
  }
  const shouldStartQrLogin =
    state.vuplex?.startInQrMode &&
    (route.meta.layout !== 'auth' ||
      route.name === authRoutes.login.name ||
      route.name === authRoutes.verifyQrCode.name)
  if (shouldStartQrLogin) {
    if (!state.permissionsAccepted) {
      return authRoutes.permissionsDenied.path
    }
    questBridge.scanQrCode()
    return authRoutes.verifyQrCode.path
  }
  if (route.meta.layout !== 'auth') {
    return authRoutes.login.path
  }
}

async function tryFetchUser(organizationId: string | string[] | undefined) {
  const abortController = new AbortController()
  const tenSecondsInMs = 10000
  const timeoutInMs = window.testConfig?.fetchUserTimeoutInMilliseconds ?? tenSecondsInMs
  const timeout = setTimeout(() => abortController.abort(), timeoutInMs)
  try {
    return await postGetBody<AuthenticatedResponse | ProblemDetailsResponse>(
      'fetch-user',
      { organizationId },
      { logoutOn401: false, abortController }
    )
  } catch (e) {
    trackEvent('FetchUserFailed', { error: String(e) })
    return undefined
  } finally {
    clearTimeout(timeout)
  }
}

export async function authenticated(response: AuthenticatedResponse, authType: AuthType, path?: string) {
  setState(response)
  if (authType != 'fetch-user') {
    trackEvent('LoginOption', { type: authType })
  }
  await checkCachedOrganizationId()
  questBridge.authenticated({
    user: response.user,
    organizationId: response.organizationId,
    userSettings: response.userSettings
  })
  void signalrService.initialize()
  return redirectPath(path)
}

async function checkCachedOrganizationId() {
  const cachedOrgId = await cache.get<string>('currentOrganizationId')
  if (cachedOrgId && cachedOrgId !== state.organizationId) {
    reset()
    await cache.clear()
  }
  await cache.set<string>('currentOrganizationId', state.organizationId!)
}

export async function logOut() {
  await post('logout', undefined, { logoutOn401: false })
  state.user = undefined
  state.organizations = {}
  state.organizationId = undefined
  state.featureSwitches = new Set()
  state.survey = undefined
  state.surveyAnswers = {}
  state.latestPlayerBuildSubmittedToMeta = undefined
  await router.push(authRoutes.login)
}

function setState({
  user,
  organizations,
  featureSwitches,
  organizationId,
  permissionGroups,
  survey,
  latestPlayerBuildSubmittedToMeta
}: AuthenticatedResponse) {
  state.user = user
  state.adminPermissions = mapAdminPermissions(user.permissionGroupIds, permissionGroups)
  state.organizationPermissions = mapOrganizationPermissions(user.organizationPermissions, permissionGroups)
  state.organizations = arrayToEntityDict(organizations)
  state.featureSwitches = new Set(featureSwitches)
  localSettings?.featureSwitches?.forEach(featureSwitch => {
    if (featureSwitch.on) {
      state.featureSwitches.add(featureSwitch.name)
    } else {
      state.featureSwitches.delete(featureSwitch.name)
    }
  })
  state.organizationId = organizationId
  state.permissionGroups = arrayToEntityDict(permissionGroups)
  state.survey = survey ?? undefined
  state.latestPlayerBuildSubmittedToMeta = latestPlayerBuildSubmittedToMeta ?? undefined
}

function redirectPath(path?: string) {
  const redirect = localStorage.getItem('redirect')
  localStorage.removeItem('redirect')
  const resolvedRoute = router.resolve(path ?? redirect ?? '')
  const organizationId = resolvedRoute.query['healthstream']
    ? state.organizationId
    : resolvedRoute.params.organizationId
  const invalidOrganizationId = Boolean(organizationId) && organizationId !== state.organizationId
  return invalidOrganizationId
    ? appRoutes.root.path
    : resolvedRoute.query['healthstream']
      ? resolvedRoute.href
      : (path ?? redirect ?? appRoutes.root.path)
}

async function autoLocalAuth(auth: LocalAuth, path: string) {
  const { email, password, redirectPath } = auth
  const response = await postGetBody<AuthenticatedResponse>('login', { email, password })
  if (response) {
    return authenticated(response, 'direct', redirectPath ?? path)
  }
}
